import React, { useEffect, useState } from "react";
import axios from "axios";
import { LANDING } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INTERESTS = [
  { id: "investor", label: "Investor Access" },
  { id: "family_office", label: "Family Office Mandate" },
  { id: "strategic", label: "Strategic Partnership" },
  { id: "placement", label: "Placement Agent" },
  { id: "general", label: "General Enquiry" },
];

const InvestorAccessModal = ({ open, onClose, defaultInterest }) => {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    firm: "",
    role: "",
    interest: "investor",
    notes: "",
  });
  const [status, setStatus] = useState({ state: "idle", message: "" });

  useEffect(() => {
    if (open) {
      setForm((s) => ({ ...s, interest: defaultInterest || "investor" }));
      setStatus({ state: "idle", message: "" });
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open, defaultInterest]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim() || !form.firm.trim()) {
      setStatus({
        state: "error",
        message: "Please complete name, email and firm to proceed.",
      });
      return;
    }
    setStatus({ state: "loading", message: "" });
    try {
      await axios.post(`${API}/investor-access`, form);
      setStatus({
        state: "success",
        message:
          "Request received. The partnership will respond on a confidential basis where appropriate.",
      });
      setForm({
        full_name: "",
        email: "",
        firm: "",
        role: "",
        interest: "investor",
        notes: "",
      });
    } catch (err) {
      setStatus({
        state: "error",
        message:
          "Submission failed. Please retry, or write to investors@krprivatecapital.com.",
      });
    }
  };

  if (!open) return null;

  const isFamilyOffice = defaultInterest === "family_office";

  return (
    <div
      data-testid={LANDING.modalRoot}
      className="fixed inset-0 z-[100] flex items-end md:items-center justify-center"
    >
      {/* backdrop */}
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        data-testid={LANDING.modalBackdrop}
        className="absolute inset-0 bg-black/80 backdrop-blur-md"
      />
      {/* dialog */}
      <div
        role="dialog"
        aria-modal="true"
        data-testid={LANDING.modalDialog}
        className="relative w-full md:max-w-[720px] max-h-[92vh] overflow-y-auto bg-[var(--kr-bg-2)] border border-[var(--kr-line)] shadow-2xl"
      >
        <div className="p-8 md:p-12">
          <div className="flex items-start justify-between mb-10">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                  Confidential
                </span>
                <span className="w-8 h-px bg-[var(--kr-gold)]/60" />
                <span className="font-mono-label text-[10px] text-[var(--kr-text-muted)]">
                  By Introduction
                </span>
              </div>
              <h3
                data-testid={LANDING.modalHeading}
                className="font-serif-display text-[2rem] md:text-[2.4rem] text-[var(--kr-text)] leading-[1.1]"
              >
                {isFamilyOffice ? (
                  <>
                    Family Office Mandate{" "}
                    <span className="italic text-[var(--kr-gold)]">Enquiry.</span>
                  </>
                ) : (
                  <>
                    Investor Access{" "}
                    <span className="italic text-[var(--kr-gold)]">Request.</span>
                  </>
                )}
              </h3>
            </div>
            <button
              type="button"
              onClick={onClose}
              data-testid={LANDING.modalClose}
              className="p-2 text-[var(--kr-text-muted)] hover:text-[var(--kr-gold)] transition-colors"
              aria-label="Close"
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.4"
              >
                <path d="M5 5l14 14M19 5L5 19" />
              </svg>
            </button>
          </div>

          <p
            data-testid={LANDING.modalIntro}
            className="text-[var(--kr-text-muted)] text-[14.5px] leading-[1.75] mb-10 max-w-[58ch]"
          >
            {isFamilyOffice
              ? "Tell us briefly about your family office, investment objectives and areas of interest. The partnership will review your enquiry and, where appropriate, arrange a confidential introductory discussion."
              : "Materials are reserved for eligible, sophisticated, professional or institutional investors. Submitted information will be reviewed in confidence; further information will be shared only where appropriate and subject to applicable requirements."}
          </p>

          <form onSubmit={submit} data-testid={LANDING.modalForm}>
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
              <div>
                <label className="font-mono-label text-[10px] text-[var(--kr-text-muted)] block mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  required
                  value={form.full_name}
                  onChange={update("full_name")}
                  data-testid={LANDING.modalName}
                  className="kr-input"
                />
              </div>
              <div>
                <label className="font-mono-label text-[10px] text-[var(--kr-text-muted)] block mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={update("email")}
                  data-testid={LANDING.modalEmail}
                  className="kr-input"
                />
              </div>
              <div>
                <label className="font-mono-label text-[10px] text-[var(--kr-text-muted)] block mb-2">
                  Firm / Institution *
                </label>
                <input
                  type="text"
                  required
                  value={form.firm}
                  onChange={update("firm")}
                  data-testid={LANDING.modalFirm}
                  className="kr-input"
                />
              </div>
              <div>
                <label className="font-mono-label text-[10px] text-[var(--kr-text-muted)] block mb-2">
                  Role (Optional)
                </label>
                <input
                  type="text"
                  value={form.role}
                  onChange={update("role")}
                  data-testid={LANDING.modalRole}
                  className="kr-input"
                />
              </div>
            </div>

            <div className="mt-10">
              <label className="font-mono-label text-[10px] text-[var(--kr-text-muted)] block mb-4">
                Nature of Interest
              </label>
              <div className="flex flex-wrap gap-3">
                {INTERESTS.map((i) => {
                  const selected = form.interest === i.id;
                  return (
                    <button
                      key={i.id}
                      type="button"
                      onClick={() =>
                        setForm((s) => ({ ...s, interest: i.id }))
                      }
                      data-testid={`${LANDING.modalInterestPrefix}${i.id}`}
                      className={`px-4 py-2 border text-[12px] tracking-[0.12em] uppercase font-mono-label transition-all duration-300 ${
                        selected
                          ? "border-[var(--kr-gold)] text-[var(--kr-gold)] bg-[var(--kr-gold-soft)]"
                          : "border-[var(--kr-line)] text-[var(--kr-text-muted)] hover:border-[var(--kr-gold)]/60 hover:text-[var(--kr-text)]"
                      }`}
                    >
                      {i.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-10">
              <label className="font-mono-label text-[10px] text-[var(--kr-text-muted)] block mb-2">
                Notes (Optional)
              </label>
              <textarea
                rows={4}
                value={form.notes}
                onChange={update("notes")}
                data-testid={LANDING.modalNotes}
                className="kr-input resize-none"
              />
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-6">
              <button
                type="submit"
                disabled={status.state === "loading"}
                data-testid={LANDING.modalSubmit}
                className="kr-btn-primary disabled:opacity-60"
              >
                {status.state === "loading" ? "Submitting…" : "Submit Request"}
                <span aria-hidden>→</span>
              </button>
              {status.message && (
                <p
                  data-testid={LANDING.modalStatus}
                  className={`text-[13px] max-w-[40ch] ${
                    status.state === "success"
                      ? "text-[var(--kr-gold)]"
                      : "text-[#d27a7a]"
                  }`}
                >
                  {status.message}
                </p>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default InvestorAccessModal;
